import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Cpu, Download, Link2, RefreshCw } from 'lucide-react';
import WiringConnectionEditor from '../components/WiringConnectionEditor';
import { boardsAPI } from '../services/api';
import { appUrl } from '../utils/appPaths';
import { PUBLIC_CONTENT_PAGE_MAX_WIDTH } from '../utils/publicPageLayout';
import { usePageSeo } from '../utils/seo';
import { downloadSvgElementAsPng } from '../utils/svgExport';
import { appendConnectionsTableToExportSvg } from '../utils/wiringExportTable';

const SENSOR_CATEGORIES = new Set(['sensor-module']);
const MICROCONTROLLER_CATEGORIES = new Set([
  'microcontroller-8bit',
  'microcontroller-16bit',
  'microcontroller-32bit',
  'development-board',
]);

const PageContainer = styled.div`
  width: min(${PUBLIC_CONTENT_PAGE_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  padding: clamp(24px, 4vw, 48px) 0 clamp(32px, 4vw, 56px);
  display: grid;
  gap: 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
`;

const StepCard = styled.section`
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: white;
  padding: clamp(18px, 3vw, 24px);
  display: grid;
  gap: 18px;
  box-shadow: 0 12px 30px -24px rgba(15, 23, 42, 0.4);
`;

const StepHeader = styled.div`
  display: grid;
  gap: 8px;
`;

const StepTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
`;

const StepDescription = styled.p`
  margin: 0;
  color: #64748b;
  line-height: 1.6;
`;

const SelectorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const SelectorField = styled.label`
  display: grid;
  gap: 10px;
`;

const SelectorLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #334155;
`;

const BoardSelect = styled.select`
  width: 100%;
  min-height: 48px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: white;
  color: #0f172a;
  padding: 0 14px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const HelperText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const InlineMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 13px;
`;

const InlineBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
`;

const StatusCard = styled.div`
  border-radius: 14px;
  padding: 18px 20px;
  border: 1px solid ${(props) => props.$error ? '#fecaca' : '#dbeafe'};
  background: ${(props) => props.$error ? '#fef2f2' : '#f8fbff'};
  color: ${(props) => props.$error ? '#b91c1c' : '#1e40af'};
  line-height: 1.6;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const RetryButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  background: #0f172a;
  color: white;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background: #1e293b;
  }
`;

const LoadingBlock = styled.div`
  padding: 12px 0;
  color: #64748b;
  font-size: 14px;
`;

const EmptyEditorState = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 28px 22px;
  background: #f8fafc;
  color: #64748b;
  line-height: 1.7;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  color: #0f172a;
  font-size: 14px;
  line-height: 1.5;
  cursor: pointer;

  input {
    margin-top: 2px;
    width: 16px;
    height: 16px;
    accent-color: #2563eb;
    cursor: pointer;
  }
`;

const CheckboxLabel = styled.span`
  display: grid;
  gap: 4px;
`;

const CheckboxHint = styled.span`
  color: #64748b;
  font-size: 13px;
`;

const DownloadButton = styled.button`
  border: none;
  border-radius: 14px;
  padding: 12px 18px;
  background: #0f172a;
  color: white;
  font-size: 14px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  &:hover {
    background: #1e293b;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DownloadHint = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const boardNameIncludes = (board, values) => {
  const name = String(board?.name || '').toLowerCase();
  return values.some((value) => name.includes(value));
};

const isSensorBoard = (board) => {
  const category = String(board?.category || '').toLowerCase();
  return SENSOR_CATEGORIES.has(category)
    || boardNameIncludes(board, ['sensor', 'module', 'shield']);
};

const isMicrocontrollerBoard = (board) => {
  const category = String(board?.category || '').toLowerCase();
  return MICROCONTROLLER_CATEGORIES.has(category)
    || boardNameIncludes(board, ['arduino', 'esp', 'raspberry', 'microcontroller', 'board']);
};

const sortBoardsByName = (boards) => [...boards].sort((left, right) => (
  String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' })
));

const formatBoardOptionLabel = (board) => {
  const boardName = String(board?.name || 'Unnamed board').trim();
  const manufacturer = String(board?.manufacturer || '').trim();
  return manufacturer ? `${boardName} (${manufacturer})` : boardName;
};

const formatConnectionPinLabel = (pin, fallbackId) => {
  const pinName = String(pin?.pin_name ?? pin?.pinName ?? pin?.pin_number ?? pin?.pinNumber ?? fallbackId ?? 'Pin').trim();
  const pinNumber = String(pin?.pin_number ?? pin?.pinNumber ?? '').trim();

  if (!pinNumber || pinNumber === pinName) {
    return pinName || String(fallbackId || 'Pin');
  }

  return `${pinName} (${pinNumber})`;
};

const createHiddenSvgHost = () => {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.position = 'fixed';
  host.style.left = '-100000px';
  host.style.top = '-100000px';
  host.style.opacity = '0';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'hidden';
  host.style.visibility = 'hidden';
  return host;
};

const downloadSvgMarkupAsPng = async (svgMarkup, {
  fileName,
  includeConnectionsTable = false,
  connections = [],
} = {}) => {
  if (typeof document === 'undefined') {
    throw new Error('Document is not available for export.');
  }

  const host = createHiddenSvgHost();
  host.innerHTML = String(svgMarkup || '').trim();
  document.body.appendChild(host);

  try {
    const svgElement = host.querySelector('svg');
    if (!(svgElement instanceof SVGElement)) {
      throw new Error('Unable to render the generated wiring diagram for export.');
    }

    await downloadSvgElementAsPng(svgElement, {
      fileName,
      prepareClone: includeConnectionsTable
        ? (clone, geometry) => {
            appendConnectionsTableToExportSvg(clone, {
              connections,
              viewBox: geometry?.viewBox,
            });
          }
        : undefined,
    });
  } finally {
    host.remove();
  }
};

const WiringSuggest = () => {
  const [sensorBoards, setSensorBoards] = useState([]);
  const [microcontrollerBoards, setMicrocontrollerBoards] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState('');
  const [selectedMicrocontroller, setSelectedMicrocontroller] = useState('');
  const [selectedSensorBoard, setSelectedSensorBoard] = useState(null);
  const [selectedMicrocontrollerBoard, setSelectedMicrocontrollerBoard] = useState(null);
  const [sensorPins, setSensorPins] = useState([]);
  const [microcontrollerPins, setMicrocontrollerPins] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [loadingSelection, setLoadingSelection] = useState({ sensor: false, microcontroller: false });
  const [boardsError, setBoardsError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const [generatedSvgMarkup, setGeneratedSvgMarkup] = useState('');
  const [includeWiringTable, setIncludeWiringTable] = useState(false);
  const [downloadingPng, setDownloadingPng] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  usePageSeo({
    title: 'Wiring Suggest | MicroPinouts',
    description: 'Choose a sensor board and microcontroller to explore suggested wiring connections and pin mappings.',
    keywords: 'wiring suggest, wiring suggestions, sensor wiring, microcontroller pin mapping, MicroPinouts',
    canonicalUrl: appUrl('/WiringSuggest'),
    ogType: 'website',
  });

  const loadBoards = useCallback(async () => {
    setLoadingBoards(true);
    setBoardsError('');

    try {
      const response = await boardsAPI.getAll({ include_svg: 0 });
      const allBoards = Array.isArray(response?.data) ? response.data : [];

      setSensorBoards(sortBoardsByName(allBoards.filter(isSensorBoard)));
      setMicrocontrollerBoards(sortBoardsByName(allBoards.filter(isMicrocontrollerBoard)));
    } catch (error) {
      console.error('Failed to load wiring suggestion boards:', error);
      setBoardsError('Unable to load published boards right now. Please try again in a moment.');
    } finally {
      setLoadingBoards(false);
    }
  }, []);

  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  const loadBoardSelection = useCallback(async (boardId, type) => {
    const resetSelection = () => {
      if (type === 'sensor') {
        setSelectedSensorBoard(null);
        setSensorPins([]);
      } else {
        setSelectedMicrocontrollerBoard(null);
        setMicrocontrollerPins([]);
      }
    };

    if (!boardId) {
      resetSelection();
      return;
    }

    resetSelection();
    setSelectionError('');
    setLoadingSelection((current) => ({ ...current, [type]: true }));

    try {
      const [boardResponse, pinsResponse] = await Promise.all([
        boardsAPI.getBoard(boardId),
        boardsAPI.getPins(boardId),
      ]);

      const boardData = boardResponse?.data || null;
      const pinData = Array.isArray(pinsResponse?.data) ? pinsResponse.data : [];

      if (type === 'sensor') {
        setSelectedSensorBoard(boardData);
        setSensorPins(pinData);
      } else {
        setSelectedMicrocontrollerBoard(boardData);
        setMicrocontrollerPins(pinData);
      }
    } catch (error) {
      console.error(`Failed to load ${type} board selection:`, error);
      resetSelection();
      setSelectionError(`Unable to load the selected ${type} board. Please choose another board or try again.`);
    } finally {
      setLoadingSelection((current) => ({ ...current, [type]: false }));
    }
  }, []);

  const handleSensorChange = useCallback((event) => {
    const nextBoardId = event.target.value;
    setSelectedSensor(nextBoardId);
    setConnections([]);
    setGeneratedSvgMarkup('');
    setDownloadError('');
    loadBoardSelection(nextBoardId, 'sensor');
  }, [loadBoardSelection]);

  const handleMicrocontrollerChange = useCallback((event) => {
    const nextBoardId = event.target.value;
    setSelectedMicrocontroller(nextBoardId);
    setConnections([]);
    setGeneratedSvgMarkup('');
    setDownloadError('');
    loadBoardSelection(nextBoardId, 'microcontroller');
  }, [loadBoardSelection]);

  const handleConnectionsChange = useCallback((nextConnectionsOrUpdater) => {
    setConnections((currentConnections) => {
      const nextConnections = typeof nextConnectionsOrUpdater === 'function'
        ? nextConnectionsOrUpdater(currentConnections)
        : nextConnectionsOrUpdater;

      return Array.isArray(nextConnections) ? nextConnections : [];
    });
  }, []);

  const boardCounts = useMemo(() => ({
    sensors: sensorBoards.length,
    microcontrollers: microcontrollerBoards.length,
  }), [microcontrollerBoards.length, sensorBoards.length]);

  const sensorPinsById = useMemo(
    () => new Map(sensorPins.map((pin) => [String(pin.id), pin])),
    [sensorPins]
  );

  const microcontrollerPinsById = useMemo(
    () => new Map(microcontrollerPins.map((pin) => [String(pin.id), pin])),
    [microcontrollerPins]
  );

  const completedConnections = useMemo(
    () => connections.filter((connection) => connection?.sensorPin && connection?.microcontrollerPin),
    [connections]
  );

  const downloadTableConnections = useMemo(
    () => completedConnections.map((connection) => ({
      sensorPin: formatConnectionPinLabel(
        sensorPinsById.get(String(connection.sensorPin)),
        connection.sensorPin
      ),
      microcontrollerPin: formatConnectionPinLabel(
        microcontrollerPinsById.get(String(connection.microcontrollerPin)),
        connection.microcontrollerPin
      ),
    })),
    [completedConnections, microcontrollerPinsById, sensorPinsById]
  );

  const editorReady = Boolean(
    selectedSensorBoard
    && selectedMicrocontrollerBoard
    && !loadingSelection.sensor
    && !loadingSelection.microcontroller
  );

  const canDownloadDiagram = Boolean(editorReady && generatedSvgMarkup && completedConnections.length > 0);

  const handleDownloadDiagram = useCallback(async () => {
    if (!canDownloadDiagram) {
      return;
    }

    setDownloadingPng(true);
    setDownloadError('');

    try {
      const sensorName = selectedSensorBoard?.name || 'sensor';
      const microcontrollerName = selectedMicrocontrollerBoard?.name || 'microcontroller';
      await downloadSvgMarkupAsPng(generatedSvgMarkup, {
        fileName: `${sensorName}-to-${microcontrollerName}-wiring${includeWiringTable ? '-with-table' : ''}.png`,
        includeConnectionsTable: includeWiringTable,
        connections: downloadTableConnections,
      });
    } catch (error) {
      console.error('Failed to download wiring diagram PNG:', error);
      setDownloadError('Unable to download the wiring diagram right now. Please try again.');
    } finally {
      setDownloadingPng(false);
    }
  }, [
    canDownloadDiagram,
    downloadTableConnections,
    generatedSvgMarkup,
    includeWiringTable,
    selectedMicrocontrollerBoard?.name,
    selectedSensorBoard?.name,
  ]);

  return (
    <PageContainer>
      <Header>
        <Title>Wiring Suggestions</Title>
        <Subtitle>
          Find likely pin connections before you build.
        </Subtitle>
      </Header>

      <StepCard>
        <StepHeader>
          <StepTitle>
            <Cpu size={18} />
            Step 1: Select Boards
          </StepTitle>
          <StepDescription>
            Choose the published boards you want to compare. We will load their pin data and board SVGs for the wiring workspace.
          </StepDescription>
        </StepHeader>

        {boardsError ? (
          <StatusCard $error>
            {boardsError}
            <ActionRow style={{ marginTop: '12px' }}>
              <RetryButton type="button" onClick={loadBoards}>
                <RefreshCw size={15} />
                Retry
              </RetryButton>
            </ActionRow>
          </StatusCard>
        ) : (
          <>
            <SelectorGrid>
              <SelectorField>
                <SelectorLabel>Sensor Board</SelectorLabel>
                <BoardSelect value={selectedSensor} onChange={handleSensorChange} disabled={loadingBoards}>
                  <option value="">Select a sensor board...</option>
                  {sensorBoards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {formatBoardOptionLabel(board)}
                    </option>
                  ))}
                </BoardSelect>
              </SelectorField>

              <SelectorField>
                <SelectorLabel>Microcontroller Board</SelectorLabel>
                <BoardSelect value={selectedMicrocontroller} onChange={handleMicrocontrollerChange} disabled={loadingBoards}>
                  <option value="">Select a microcontroller board...</option>
                  {microcontrollerBoards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {formatBoardOptionLabel(board)}
                    </option>
                  ))}
                </BoardSelect>
              </SelectorField>
            </SelectorGrid>

            <InlineMeta>
              <InlineBadge>{boardCounts.sensors} sensor boards</InlineBadge>
              <InlineBadge>{boardCounts.microcontrollers} microcontroller boards</InlineBadge>
              {loadingBoards && <span>Loading published board catalog...</span>}
            </InlineMeta>

            <HelperText>
              Tip: once both boards are loaded, open the editor below and use the <strong>Suggest Wiring</strong> control to generate likely matches.
            </HelperText>
          </>
        )}
      </StepCard>

      <StepCard>
        <StepHeader>
          <StepTitle>
            <Link2 size={18} />
            Step 2: Review Suggested Wiring
          </StepTitle>
          <StepDescription>
            The editor supports drag-to-connect wiring, but the suggestion controls are the fastest way to surface probable pin pairings.
          </StepDescription>
        </StepHeader>

        {selectionError && (
          <StatusCard $error>{selectionError}</StatusCard>
        )}

        {(loadingSelection.sensor || loadingSelection.microcontroller) && (
          <LoadingBlock>
            Loading board data for the wiring workspace...
          </LoadingBlock>
        )}

        {!editorReady ? (
          <EmptyEditorState>
            Select both a sensor board and a microcontroller board to open the wiring suggestion workspace.
          </EmptyEditorState>
        ) : (
          <WiringConnectionEditor
            sensorBoard={selectedSensorBoard}
            microcontrollerBoard={selectedMicrocontrollerBoard}
            sensorPins={sensorPins}
            microcontrollerPins={microcontrollerPins}
            connections={connections}
            onConnectionsChange={handleConnectionsChange}
            onGeneratedSvgChange={setGeneratedSvgMarkup}
            emphasizeSuggestions
          />
        )}
      </StepCard>

      <StepCard>
        <StepHeader>
          <StepTitle>
            <Download size={18} />
            Download Wiring Diagram
          </StepTitle>
          <StepDescription>
            Export the current generated wiring diagram as a PNG. This export uses the generated SVG itself, so the on-canvas rotate, resize, and zoom controls are not included in the image.
          </StepDescription>
        </StepHeader>

        {!canDownloadDiagram ? (
          <EmptyEditorState>
            Save at least one wiring connection in the editor above to enable PNG download.
          </EmptyEditorState>
        ) : (
          <>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={includeWiringTable}
                onChange={(event) => setIncludeWiringTable(event.target.checked)}
                disabled={downloadingPng}
              />
              <CheckboxLabel>
                <span>Include generated wiring table</span>
                <CheckboxHint>
                  Checked adds the pin connection table below the diagram. Unchecked exports only the wiring diagram.
                </CheckboxHint>
              </CheckboxLabel>
            </CheckboxRow>

            <ActionRow>
              <DownloadButton type="button" onClick={handleDownloadDiagram} disabled={downloadingPng}>
                <Download size={16} />
                {downloadingPng ? 'Preparing PNG...' : 'Download Wiring Diagram (.PNG)'}
              </DownloadButton>
            </ActionRow>

            <DownloadHint>
              The export uses your current saved wiring connections and board layout.
            </DownloadHint>
          </>
        )}

        {downloadError && (
          <StatusCard $error>{downloadError}</StatusCard>
        )}
      </StepCard>
    </PageContainer>
  );
};

export default WiringSuggest;
